// A curator's Toolset flow in plain words: what blocks a toolset, what to do
// about it, where a hub stands with consumers, the commands that push a
// toolset, and the questions a test run missed. Pure, so each is tested
// without a screen. See Design > Flows, "Toolset flow", and Design > Names and
// definitions, "Blocker" and "Listing state".

import { Match } from "effect";
import type { ActivationSummary, Blocker, HubSecretState, Listing, RequestRepairResponse, TestRunSummary, ToolsetSummary } from "../../../src/exchange/curator-api.ts";
import { describeFailure } from "../api.ts";
import type { ApiFailure, HubRecord, HubSummary } from "./types.ts";

/**
 * The toolset the hub's manifest belongs to: its active or newest staged
 * toolset, whichever has the manifest's version as its commit. For an
 * archived hub whose newest toolset was once active, that's neither, so it
 * has none.
 */
export const toolsetInUse = ({ manifest, active, newestStaged }: HubSummary): ToolsetSummary | null =>
  [active, newestStaged].find((t) => t?.commit === manifest.version) ?? null;

/** What a curator does next about a blocker, which a screen turns into a button. */
export type BlockerAction = "grant" | "runExamples" | "runGolden" | "wait" | "none";

/**
 * One unmet activation check as the curator reads it. `text` is words written
 * here from the blocker's tag and its fields, never the Exchange's `detail`,
 * so a screen never repeats text it didn't choose. `noActionClears` is true
 * for a blocker that no action on this toolset clears: it needs a new
 * commit, the hub registered again, or staging to finish. While one is
 * there, the other blockers' actions would spend a grant or a test run on a
 * toolset that can't be activated. Each tag says, so a new blocker must say
 * whether an action clears it.
 */
export function blockerWords(b: Blocker): { text: string; action: BlockerAction; noActionClears: boolean } {
  return Match.valueTags(b, {
    HubSecretMissing: ({ missing, unreadable }) => {
      // A grant covers only what the platform's shared token can read; the
      // rest needs the repository made public or added to that token. See
      // Design > Operations, "Hubs and public repositories".
      // The Exchange puts every unreadable repository in missing too.
      const grantable = missing.filter((r) => !unreadable.includes(r));
      const unfixable = unreadable.length === 0 ? "" : `A grant can't cover ${unreadable.join(", ")}: the platform's shared WarmHub token can't read ${unreadable.length === 1 ? "it" : "them"}. Make ${unreadable.length === 1 ? "it" : "them"} public, or ask the operator to add ${unreadable.length === 1 ? "it" : "them"} to that token, then grant access.`;
      if (grantable.length === 0) return { text: unfixable, action: "none" as const, noActionClears: false };
      const grant = `The hub can't read ${grantable.join(", ")} yet. Grant access so its tools can.`;
      return { text: unfixable === "" ? grant : `${grant} ${unfixable}`, action: "grant" as const, noActionClears: false };
    },
    ExamplesNotRun: () => ({ text: "Run the examples: ask each question's example and check the answer.", action: "runExamples" as const, noActionClears: false }),
    ExamplesRunning: () => ({ text: "The examples are running.", action: "wait" as const, noActionClears: false }),
    ExamplesFailed: () => ({ text: "The latest examples run didn't pass. Fix the toolset and push again, or run the examples again.", action: "runExamples" as const, noActionClears: false }),
    GoldenNotRun: () => ({ text: "Run the golden questions.", action: "runGolden" as const, noActionClears: false }),
    GoldenRunning: () => ({ text: "The golden questions are running.", action: "wait" as const, noActionClears: false }),
    NotStaged: ({ status }) => ({ text: status === "validating" ? "The toolset is still being staged." : "The toolset was rejected at staging. Fix its problems and push a new commit.", action: "none" as const, noActionClears: true }),
    HubArchived: () => ({ text: "The hub is archived. Register it again to activate a toolset.", action: "none" as const, noActionClears: true }),
    ToolSdkUnsupported: ({ sdkVersion, supported }) => ({
      text: `The toolset was built with Tool SDK ${sdkVersion}, which is no longer supported. Rebuild it with ${supported.join(" or ")}, push, and stage it.${
        supported.length > 0 && supported.every((v) => numberedAbove(sdkVersion, v)) ? " The Tool SDK was numbered again from 0.1.0 before its first release, so the higher number is the older build." : ""}`,
      action: "none" as const, noActionClears: true,
    }),
  });
}

// Whether version `a` is numbered above `b`, part by part ("0.10.0" above
// "0.9.0"). An unsupported version numbered above every supported one was
// built before the Tool SDK's renumbering on 15 Oct 2026 (#358), since
// staging takes no version it doesn't support. The clause goes once no
// toolset built with 1.0.0 remains, and before 1.0.0 is supported again.
const numberedAbove = (a: string, b: string) => a.localeCompare(b, "en", { numeric: true }) > 0;

/**
 * The blockers a toolset's card shows: each one, unless one that no action
 * clears is there, when only those are shown, so no action is offered that
 * can't lead to activation. See Design > Flows, "Toolset flow", "Review
 * staged toolsets".
 */
export function blockerRows(blockers: readonly Blocker[]) {
  const rows = blockers.map((b) => ({ tag: b._tag, ...blockerWords(b) }));
  return rows.some((r) => r.noActionClears) ? rows.filter((r) => r.noActionClears) : rows;
}

/**
 * The line under a toolset's commit, with `when` its time in words. A
 * toolset still staging or rejected was pushed, not staged, and stores no
 * bundles, so it has no tool count.
 */
export function toolsetNote(t: ToolsetSummary, when: string): string {
  const sdk = t.sdkVersion ? ` · Tool SDK ${t.sdkVersion}` : "";
  return t.status === "rejected" || t.status === "validating"
    ? `Pushed ${when}${sdk}`
    : `Staged ${when} · ${t.bundles} ${t.bundles === 1 ? "tool" : "tools"}${sdk}`;
}

/** How many of a rejected toolset's problems show before the rest fold away. */
export const PROBLEMS_SHOWN = 5;

/**
 * A rejected toolset's problems as its card shows them: the first few, the
 * rest folded, and when they were found. They are text that staging recorded
 * then, under its checks of the time, which may have changed since, so a
 * problem may no longer apply.
 */
export const problemsView = (problems: readonly string[], when: string) => ({
  shown: problems.slice(0, PROBLEMS_SHOWN),
  folded: problems.slice(PROBLEMS_SHOWN),
  note: `Found at staging, ${when}. Staging's checks can change, so a problem here may no longer apply. Fix what still does, and push a new commit to check it against today's checks: the same commit isn't staged twice.`,
});

/** A toolset's place in the hub's life, in the word a curator uses. A staged toolset that was active once is the one to roll back to. */
export type ToolsetPhase = "active" | "staging" | "rejected" | "ready" | "waiting" | "rollback";

export function phaseOf(t: ToolsetSummary): ToolsetPhase {
  if (t.status === "active") return "active";
  if (t.status === "validating") return "staging";
  if (t.status === "rejected") return "rejected";
  if (t.activatedAt !== null) return "rollback";
  return t.canActivate ? "ready" : "waiting";
}

export const PHASE_WORDS: Record<ToolsetPhase, string> = {
  active: "Active", staging: "Staging", rejected: "Rejected", ready: "Ready to activate", waiting: "Needs work", rollback: "Was active",
};

/**
 * The button on a toolset that can be activated. It is a rollback only when
 * the toolset was active before the one active now; one that was active
 * after it is activated again, not rolled back to.
 */
export const activateWords = (t: ToolsetSummary, active: ToolsetSummary | null) =>
  t.activatedAt === null ? "Activate"
    : active?.activatedAt != null && t.activatedAt < active.activatedAt ? "Roll back to this toolset" : "Activate again";

/** One line of the activation history as the curator reads it. */
export type HistoryRow = { commit: string; at: number; replaced: string | null; recorded: boolean };

/**
 * The hub's activations for display, newest first. The history starts with
 * migration 0013, so the toolset active now may have no row: its own
 * `activatedAt` stands in, marked as not recorded. A row with no replaced
 * toolset is the hub's first, or one copied from before the history existed.
 */
export function historyRows(activations: readonly ActivationSummary[], active: ToolsetSummary | null): HistoryRow[] {
  const rows = activations.map((a) => ({ commit: a.commit, at: a.at, replaced: a.replacedCommit, recorded: true }));
  const missing = active !== null && active.activatedAt !== null && !activations.some((a) => a.commit === active.commit);
  return missing ? [{ commit: active.commit, at: active.activatedAt ?? 0, replaced: null, recorded: false }, ...rows] : rows;
}

/** What a history row says about what it replaced. */
export const replacedWords = (r: HistoryRow) => (r.replaced !== null ? `replaced ${r.replaced.slice(0, 7)}` : r.recorded ? "no earlier toolset recorded" : "activation not recorded in the history");

/** Where a hub stands with consumers, and what its curator may do. First listing is approved by the WarmHub team, never from Studio. */
export type ListingView = { label: string; tone: "neutral" | "info" | "success"; text: string; action: "unlist" | "relist" | null };

export const LISTING_VIEWS: Record<Listing, (note: string | null) => ListingView> = {
  never_listed: (note) => ({ label: "Not listed yet", tone: "neutral", text: note
    ? `Consumers can't reach this hub. The WarmHub team didn't approve its first listing: ${note} Fix what it names, then ask again.`
    : "Consumers can't reach this hub yet. Activate a toolset to ask for its first listing.", action: null }),
  awaiting_approval: () => ({ label: "Waiting for approval", tone: "info", text: "The toolset is active. The WarmHub team approves a hub's first listing, then consumers can reach it.", action: null }),
  listed: () => ({ label: "Listed", tone: "success", text: "Consumers can reach this hub. You can unlist it at any time.", action: "unlist" }),
  unlisted: (note) => ({ label: "Unlisted", tone: "neutral", text: `Consumers can't reach this hub.${note ? ` Reason given: ${note}` : ""} List it again when you're ready; it needs no new approval.`, action: "relist" }),
  archived: () => ({ label: "Archived", tone: "neutral", text: "This hub is archived. Register its repository again to bring it back.", action: null }),
};

export const listingView = (record: HubRecord): ListingView => LISTING_VIEWS[record.listing](record.listingNote);

/** Who can find a hub, and why a hub limited to its org reaches nobody, from its summary's `audienceBlocked`. See Design > Exchange, "Hub audiences". */
export type AudienceView = { label: string; tone: "neutral" | "success" | "danger"; text: string };
export function audienceView(record: HubRecord, blocked: HubSummary["audienceBlocked"] | undefined): AudienceView {
  const [org = "", repo = record.name] = record.name.split("/");
  if (record.audience.kind === "everyone") return { label: "Everyone", tone: "neutral", text: "Anyone signed in to WarmHub can find this hub once it's listed." };
  const members = { label: `${org} only`, tone: "success" as const, text: `Only people who can read ${org}/${repo} in WarmHub find this hub. Everyone else sees it as if it didn't exist.` };
  return Match.value(blocked ?? null).pipe(
    Match.when(null, () => members),
    Match.when("PublicRepository", () => ({ label: "Reaches no one", tone: "danger" as const, text: `${org}/${repo} is public now, so the hub reaches no one. Make the repository private in WarmHub, or open the hub to everyone.` })),
    Match.when("VisibilityUnread", () => ({ label: "Reaches no one", tone: "danger" as const, text: `The Exchange can't read whether ${org}/${repo} is private, so the hub reaches no one. Check that the hub has access, on this page, and that the repository wasn't renamed.` })),
    Match.exhaustive,
  );
}

/**
 * The commands that clone a hub's toolset repository and push to it with a
 * push token. The token travels in an environment variable and git's
 * `http.extraHeader`, as the Exchange's own scripts send it, so it never
 * lands in a remote URL or in git's config.
 */
export function pushCommands(remote: string) {
  const withToken = 'GIT_CONFIG_COUNT=1 GIT_CONFIG_KEY_0=http.extraHeader GIT_CONFIG_VALUE_0="Authorization: Bearer $PUSH_TOKEN"';
  return {
    setup: "read -rs PUSH_TOKEN && export PUSH_TOKEN",
    clone: `${withToken} git clone '${remote}' hub-toolset && cd hub-toolset`,
    push: `${withToken} git push origin HEAD:main`,
  };
}

/** A run's questions that didn't pass, with what each needs to be found again: its outcome, its note, the stage it failed at and the request ID it was answered under. */
export const missesOf = <R extends { pass: boolean }>(results: readonly R[] | null): R[] => (results ?? []).filter((r) => !r.pass);

/** Where in answering a question a test run's miss failed, in plain words, by the stage the run records. */
const STAGE_WORDS = new Map(Object.entries({
  Match: "finding the right question", Router: "choosing a tool", Resolve: "reading the hub's data", Tool: "running the tool",
  Validate: "checking the answer's shape", ToolContract: "the tool's contract", Settle: "settling the answer", Deadline: "taking too long",
}));
export const stageWords = (stage: string) => STAGE_WORDS.get(stage) ?? "answering";

/** How a run reads in words. */
export function runWords(run: TestRunSummary | null, kindWord: string): string {
  if (run === null) return `${kindWord} not run yet`;
  const counts = run.passed !== null && run.total !== null ? `${run.passed} of ${run.total} passed` : "";
  return Match.value(run.status).pipe(
    Match.when("running", () => `${kindWord} running`),
    Match.when("passed", () => `${kindWord}: ${counts}`.trimEnd()),
    Match.when("failed", () => `${kindWord}: ${counts}`.trimEnd()),
    Match.when("error", () => `${kindWord} ended in an error`),
    Match.exhaustive,
  );
}

/** What a curator does in the Toolset flow that can be refused, and connecting or disconnecting the hub's Hub token, which lets its hub agent work. */
export type Change = "register" | "pushToken" | "grant" | "stage" | "testRun" | "readRun" | "activate" | "list" | "unlist" | "connect" | "disconnect" | "audience" | "repair";

// Words for the refusals a curator can act on, by the tag the Exchange
// answers with. Anything else falls back to the Exchange's own authored
// detail and the request ID, which describeFailure adds.
const REFUSALS: Record<Change, Record<string, string>> = {
  register: {
    HubNotFound: "Studio can't see that repository. Check the name, and that your WarmHub account can read it.",
    NotRepoConfigurer: "Your WarmHub account can't configure that repository. Ask its owner for configure access, then register it.",
    HubAlreadyRegistered: "That repository is already a hub.",
    HubRepoConflict: "That hub's toolset repository already exists, so Studio can't create it again. Ask the WarmHub team to look at it.",
    Http400: "Enter the repository as org/name.",
  },
  pushToken: { HubArchived: "This hub is archived. Register its repository again first.", HubNotFound: "Your WarmHub account doesn't configure this hub's repository." },
  grant: {
    NotRepoConfigurer: "Your WarmHub account can't grant access to the repositories this hub reads. It needs configure access on each one; ask their owner, then grant again.",
    HubArchived: "This hub is archived. Register its repository again first.",
    ToolsetNotFound: "That toolset isn't the hub's anymore. Reload and try again.",
  },
  stage: { ToolsetNotFound: "The hub's toolset repository has no commit on main to stage. Push your toolset first.", HubArchived: "This hub is archived. Register its repository again first." },
  // HubSecretMissing keeps the Exchange's detail (coverageDetail), which says
  // whether a grant helps or the repository must be made readable first.
  testRun: { HubArchived: "This hub is archived. Register its repository again first." },
  readRun: { NotFound: "Studio can't find that test run.", HubNotFound: "Your WarmHub account doesn't configure this hub's repository." },
  activate: {
    ActivationRefused: "The toolset still has things to finish. Work through the list above it.",
    ToolsetChanged: "The toolset changed while you were activating it. Reload and look again.",
    InvalidListingTransition: "The hub's listing can't move that way right now.",
    HubArchived: "This hub is archived. Register its repository again first.",
  },
  list: { InvalidListingTransition: "The hub can't be listed from where it is.", HubArchived: "This hub is archived. Register its repository again first.", NotOperator: "The WarmHub team approves a hub's first listing." },
  // HubTokenRefused keeps the Exchange's detail, which says what WarmHub refused and what to do.
  connect: {
    NotRepoConfigurer: "Your WarmHub account can't configure this hub's repository, so it can't connect the hub agent. Ask the repository's owner for configure access, then connect.",
    HubArchived: "This hub is archived. Register its repository again first.",
    HubNotFound: "Your WarmHub account doesn't configure this hub's repository.",
  },
  disconnect: { HubNotFound: "Your WarmHub account doesn't configure this hub's repository." },
  // AudienceRefused keeps the Exchange's detail, which says why and what to do in WarmHub.
  audience: {
    NotRepoConfigurer: "Only a curator who can configure this hub's repository in WarmHub can change who finds it.",
    HubArchived: "This hub is archived. Register its repository again first.",
  },
  // ServiceUnavailable keeps the Exchange's detail, which says whether the hub agent isn't on for the hub or didn't keep the request.
  repair: {
    NotRepoConfigurer: "Only a curator who can configure this hub's repository in WarmHub can ask for a repair round.",
    HubArchived: "This hub is archived. Register its repository again first.",
    ToolsetNotFound: "That toolset isn't the hub's anymore. The list has been read again.",
  },
  unlist: { InvalidListingTransition: "The hub can't be unlisted from where it is.", HubArchived: "This hub is archived. Register its repository again first.", NotOperator: "The WarmHub team handles a hub's first listing." },
};

/**
 * Whether a toolset's card offers to ask the hub agent for a repair round:
 * only a staged or active toolset, the ones a round can improve, and only
 * where the card offers changes at all. Whether the hub agent built it, and
 * whether a round is left, is the hub agent's to say. See Design > Hub
 * agent, "Repair rounds".
 */
export const offersRepair = (status: ToolsetSummary["status"], readOnly: boolean): boolean =>
  !readOnly && REPAIRABLE[status];
const REPAIRABLE: Record<ToolsetSummary["status"], boolean> = { staged: true, active: true, validating: false, rejected: false };

/** What a request for a repair round came to: the hub agent answers it in an Interaction, later. */
export const REPAIR_ASKED: Record<RequestRepairResponse["result"], string> = {
  accepted: "Asked. Your hub agent will ask you to approve a repair round in Needs you, or tell you there why it can't run one.",
  duplicate: "You already asked. Your hub agent will ask you to approve a repair round in Needs you, or tell you there why it can't run one.",
};

/** A refused change in words, and the request ID so the curator can quote it. */
export function refusalWords(change: Change, e: ApiFailure): string {
  const said = REFUSALS[change][e._tag] ?? REFUSALS[change][`Http${e.status}`];
  return said === undefined ? describeFailure(e) : e.requestId ? `${said} Request ID: ${e.requestId}.` : said;
}

/** What a grant's result says about the hub's access, in words; a live or expiring grant covers the hub. */
export function accessWords(state: HubSecretState["state"]): { ok: boolean; text: string } {
  return Match.value(state).pipe(
    Match.when("Live", () => ({ ok: true, text: "Access granted." })),
    Match.when("Expiring", () => ({ ok: true, text: "Access granted. It expires soon, so grant again before then." })),
    Match.when("Expired", () => ({ ok: false, text: "The hub's access has expired. Grant it again." })),
    Match.when("Refused", () => ({ ok: false, text: "WarmHub refused the hub's access. Check that your account can configure every repository the hub reads, then grant again." })),
    Match.when("Missing", () => ({ ok: false, text: "The hub has no access yet. Grant it." })),
    Match.exhaustive,
  );
}

/** A grant's result in words, with the new token's expiry when it has one, since how long a grant lasts isn't the same everywhere. */
export function grantedWords(secret: HubSecretState, day: (ms: number) => string): ReturnType<typeof accessWords> {
  const w = accessWords(secret.state);
  if (secret.expiresAt === null || !w.ok) return w;
  const until = day(secret.expiresAt);
  return { ok: true, text: secret.state === "Expiring" ? `Access granted, but it expires ${until}, so grant again before then.` : `Access granted. It lasts until ${until}.` };
}

/**
 * The active toolset's WarmHub access as a curator reads it: a badge and a
 * line for its card, the button that grants it (`verb`) whenever it isn't
 * Live, and a Needs attention item (`attention`) when the hub stops
 * answering or is about to, with `urgency` to rank it. Missing asks for a
 * grant on the card only: the Exchange reports Missing for a toolset that
 * reads no repository, whose grant stores nothing, and for a state it
 * couldn't read, so an item there could never clear. Studio follows the
 * Curator API's state and keeps no threshold of its own: Expiring is the
 * Exchange's. See Design > Operations, "Credential check", and Design > UI,
 * "Studio hierarchy".
 */
export type AccessView = {
  label: string; tone: "success" | "warning" | "danger"; text: string; verb: string | null;
  attention: { title: string; why: string; urgency: number } | null;
};

// A state that asks for a grant says it once, as its attention item, and the card's line is that item's title and why. A higher urgency ranks first: a hub that stopped answering before one that is about to.
const needsGrant = (label: string, tone: AccessView["tone"], title: string, why: string, urgency: number): AccessView =>
  ({ label, tone, text: `${title}. ${why}`, verb: "Grant again", attention: { title, why, urgency } });
const ACCESS_VIEWS: Record<HubSecretState["state"], (until: string | null) => AccessView> = {
  Live: (until) => ({ label: "Access live", tone: "success", text: until ? `The hub can read what it needs until ${until}.` : "The hub can read what it needs.", verb: null, attention: null }),
  Expiring: (until) => needsGrant(until ? `Access expires ${until}` : "Access expiring", "warning", `Access to WarmHub expires ${until ?? "soon"}`, "Grant again before then, or the hub stops answering.", 1),
  Expired: () => needsGrant("Access expired", "danger", "The hub stopped answering: its WarmHub access expired", "Grant again to renew it.", 2),
  Refused: () => needsGrant("Access refused", "danger", "WarmHub refused the hub's access", "Check that your account can configure every repository the hub reads, then grant again.", 2),
  Missing: () => ({ label: "No access granted", tone: "warning", text: "No WarmHub access is recorded for this toolset. If its tools read WarmHub, grant it.", verb: "Grant access", attention: null }),
};

/** The view of a toolset's access, with its expiry in words by `day`. */
export const accessView = (secret: HubSecretState, day: (ms: number) => string): AccessView =>
  ACCESS_VIEWS[secret.state](secret.expiresAt === null ? null : day(secret.expiresAt));

/** The hub's active toolset's access, or null for a hub with no active toolset. */
export const activeAccess = (hub: HubSummary, day: (ms: number) => string): AccessView | null => (hub.active ? accessView(hub.active.secret, day) : null);
